import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Chip,
} from '@mui/material';
import { CheckCircle, Cancel } from '@mui/icons-material';
import { WriteoffApprovalView } from '../../../../../services/WriteoffService/types/writeoffTypes';
import { useApprovalForm } from './hooks';
import {
  ApprovalDetailsSection,
  ActionSelectionSection,
  RejectionReasonSection,
  CommentsSection,
  WarningSection,
} from './components';
import {
  DIALOG_CONFIG,
  ACTION_TYPES,
  getCurrentStatus,
  getStatusColor,
} from './constants';

export interface ApprovalDialogProps {
  open: boolean;
  onClose: () => void;
  approval: WriteoffApprovalView;
  writeoffId: string;
  company: string;
  center: string;
  writeoffAmount: number;
  readOnly?: boolean;
}

export const ApprovalDialog: React.FC<ApprovalDialogProps> = ({
  open,
  onClose,
  approval,
  writeoffId,
  company,
  center,
  writeoffAmount,
  readOnly = false,
}) => {
  const { formState, updateFormState, handleSubmit, handleClose, isValidSubmission } =
    useApprovalForm({ approval, onClose });

  const status = getCurrentStatus(approval);
  const StatusIcon = formState.action === ACTION_TYPES.APPROVE ? CheckCircle : Cancel;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      {...DIALOG_CONFIG}
      disableEscapeKeyDown={formState.isSubmitting}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" gap={1}>
          <StatusIcon
            color={getStatusColor(
              formState.action === ACTION_TYPES.APPROVE ? 'Approved' : 'Rejected'
            )}
          />
          <Typography variant="h6">
            {readOnly ? 'View' : status === 'Pending' ? 'Make' : 'Change'} Decision -{' '}
            {formState.action === ACTION_TYPES.APPROVE ? 'Approve' : 'Reject'} Writeoff
            {readOnly && (
              <Typography
                component="span"
                variant="body2"
                sx={{ ml: 1, color: '#64748b', fontWeight: 'normal' }}
              >
                (Read Only)
              </Typography>
            )}
          </Typography>
          <Chip
            label={status}
            size="small"
            color={getStatusColor(status)}
            variant="outlined"
            sx={{ ml: 'auto' }}
          />
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          <ApprovalDetailsSection approval={approval} writeoffAmount={writeoffAmount} />

          <ActionSelectionSection
            action={formState.action}
            onActionChange={(action) => updateFormState({ action })}
            isSubmitting={formState.isSubmitting}
            readOnly={readOnly}
          />

          <RejectionReasonSection
            action={formState.action}
            rejectionReason={formState.rejectionReason}
            onRejectionReasonChange={(rejectionReason) => updateFormState({ rejectionReason })}
            isSubmitting={formState.isSubmitting}
            readOnly={readOnly}
          />

          <CommentsSection
            comments={formState.comments}
            onCommentsChange={(comments) => updateFormState({ comments })}
            isSubmitting={formState.isSubmitting}
            readOnly={readOnly}
          />

          {!readOnly && <WarningSection approval={approval} />}
        </Stack>
      </DialogContent>

      <DialogActions>
        {readOnly ? (
          <Button onClick={handleClose} color="inherit">
            Close
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={formState.isSubmitting} color="inherit">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!isValidSubmission || formState.isSubmitting}
              variant="contained"
              color={formState.action === ACTION_TYPES.APPROVE ? 'success' : 'error'}
            >
              {formState.isSubmitting
                ? `${formState.action === ACTION_TYPES.APPROVE ? 'Approving' : 'Rejecting'}...`
                : `${formState.action === ACTION_TYPES.APPROVE ? 'Approve' : 'Reject'} Writeoff`}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ApprovalDialog;
